import React, { useMemo, useCallback, useContext } from 'react';
import { Button, ConfigProvider, Tooltip } from 'antd';
import type { ButtonProps as AntButtonProps } from 'antd';
import * as Icons from '@ant-design/icons';
import { useAndStore } from '../../base/store/useAndStore';
import { parseClickAction, createCommand } from '../../base/actionParser';
import { prepareAntdDomProps } from '../../utils/normalizeReactStyle';
import { DataSetInheritContext } from '../../base/DataSetInheritContext';
import type { BaseComponentProps } from '../../types/common';

/**
 * AndButton Props 接口
 */
export interface AndButtonProps extends Omit<AntButtonProps, 'onClick'>, BaseComponentProps {
  /** 按钮显示文本 */
  text?: string;
  /** 鼠标滑过按钮时显示的文字提示 */
  tooltip?: string;
  /** 图标名称，对应 @ant-design/icons 中的图标 */
  iconName?: string;
  /** 点击动作字符串，如 "submit"、"handleClick(@id)" 或 "moveUp(@index)"（迭代器内可用 @index/@dataSet/@dataSetField） */
  clickAction?: string;
  /** 是否阻止点击冒泡；未设置时，若存在 clickAction 则默认 true，避免外层行/容器 onClick 覆盖子区域动作 */
  stopClickPropagation?: boolean;
  /** 自定义点击处理函数 */
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** 迭代器内传入的当前索引，供 clickAction 中 @index 解析 */
  index?: number;
  /** 迭代器内传入的 dataSet，供 clickAction 中 @dataSet 解析 */
  dataSet?: string;
  /** 迭代器内传入的 dataSetField，供 clickAction 中 @dataSetField 解析 */
  dataSetField?: string;
  /** Design Token */
  token?: Record<string, any>;
}

/**
 * AndButton 组件
 * 封装 Ant Design Button，支持动态图标和动作解析
 */
export const AndButton: React.FC<AndButtonProps> = ({
  id,
  text,
  tooltip,
  iconName,
  clickAction,
  stopClickPropagation,
  sessionId,
  develop,
  onClick,
  children,
  index,
  dataSet,
  dataSetField,
  token,
  ...restProps
}) => {
  const addCommand = useAndStore((state) => state.addCommand);
  const ctx = useContext(DataSetInheritContext);
  const effectiveIndex = index ?? ctx?.index;
  const effectiveParentIndex = ctx?.parentIndex;

  // 订阅本组件的 props 切片，setProps(sessionId, id, props) 更新时触发重渲染
  const storeProps = useAndStore((state) =>
    sessionId && id ? state.propsStore[`${sessionId}_props`]?.[id] : null
  );

  // 合并 props
  const mergedProps = useMemo(() => {
    const baseProps = { ...restProps };
    if (storeProps && typeof storeProps === 'object') {
      Object.keys(storeProps).forEach((key) => {
        if (storeProps[key] !== undefined && storeProps[key] !== null) {
          (baseProps as any)[key] = storeProps[key];
        }
      });
    }
    // 设计器模式下禁用 disabled 属性，确保组件可被选择
    if (develop) {
      (baseProps as any).disabled = false;
    }
    return baseProps;
  }, [restProps, storeProps, develop]);

  // 处理图标
  const IconComponent = useMemo(() => {
    const name = storeProps?.iconName || iconName;
    if (name && (Icons as any)[name]) {
      return (Icons as any)[name] as React.ComponentType;
    }
    return null;
  }, [iconName, storeProps?.iconName]);

  const effectiveClickAction = useMemo(
    () => storeProps?.clickAction ?? clickAction,
    [storeProps?.clickAction, clickAction],
  );

  const effectiveDevelop = storeProps?.develop ?? develop;

  const shouldStopPropagation = useMemo(() => {
    if (effectiveDevelop) return false;
    const effectiveStop = storeProps?.stopClickPropagation ?? stopClickPropagation;
    if (effectiveStop !== undefined) return effectiveStop;
    return Boolean(effectiveClickAction);
  }, [
    effectiveDevelop,
    storeProps?.stopClickPropagation,
    stopClickPropagation,
    effectiveClickAction,
  ]);

  // 点击处理
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (onClick) {
        onClick(e);
      } else if (effectiveClickAction && sessionId) {
        const { actionName, arg } = parseClickAction(
          effectiveClickAction,
          {
            id,
            index: effectiveIndex,
            parentIndex: effectiveParentIndex,
            dataSet,
            dataSetField,
            ...mergedProps,
          },
          ctx,
        );
        const command = createCommand(actionName, arg);
        addCommand(sessionId, command);
      }

      if (shouldStopPropagation) {
        e.stopPropagation();
      }
    },
    [
      effectiveClickAction,
      sessionId,
      id,
      effectiveIndex,
      dataSet,
      dataSetField,
      effectiveParentIndex,
      mergedProps,
      addCommand,
      onClick,
      ctx,
      shouldStopPropagation,
    ],
  );

  // 显示文本
  const displayText = storeProps?.text || text;
  const hoverTooltip = storeProps?.tooltip ?? tooltip;

  const filteredToken = useMemo(() => {
    const storeToken = storeProps?.token as Record<string, any> | undefined;
    const combinedToken = { ...token, ...storeToken };
    if (!combinedToken || Object.keys(combinedToken).length === 0) return undefined;

    const result: Record<string, any> = {};
    Object.entries(combinedToken).forEach(([key, value]) => {
      if (value !== '' && value !== undefined && value !== null) {
        result[key] = value;
      }
    });

    return Object.keys(result).length > 0 ? result : undefined;
  }, [token, storeProps?.token]);

  const themeConfig = useMemo(
    () => (filteredToken ? { components: { Button: filteredToken } } : undefined),
    [filteredToken]
  );

  const buttonDomProps = useMemo(() => {
    const {
      iconPosition,
      iconPlacement,
      ...rest
    } = mergedProps as Record<string, unknown>;
    const domProps = prepareAntdDomProps(rest);
    const finalIconPlacement = iconPlacement || iconPosition;
    if (finalIconPlacement) {
      domProps.iconPlacement = finalIconPlacement;
    }
    return domProps;
  }, [mergedProps]);

  const isBlock = Boolean(buttonDomProps?.block);

  const buttonElement = (
    <Button
      {...buttonDomProps}
      icon={IconComponent ? <IconComponent /> : undefined}
      onClick={handleClick}
    >
      {displayText || children}
    </Button>
  );

  // Tooltip 包裹 disabled Button 需额外 span；同时在 block 场景保持 100% 宽度，避免布局变化。
  const contentElement = hoverTooltip ? (
    <Tooltip title={hoverTooltip}>
      <span style={{ display: isBlock ? 'block' : 'inline-block', width: isBlock ? '100%' : undefined }}>
        {buttonElement}
      </span>
    </Tooltip>
  ) : (
    buttonElement
  );

  return themeConfig ? <ConfigProvider theme={themeConfig}>{contentElement}</ConfigProvider> : contentElement;
};

export default AndButton;
